import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, ArrowLeft, TrendingUp, Award, MessageSquare, Trash2, Edit, ArrowUpDown, Download, Minus, Plus, Users } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatDateUK, getLocalTodayStr } from "@/utils/dateUtils";
import MessagePopup from "@/components/MessagePopup";
import SocialFeed from "@/components/SocialFeed";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlayerViewBest() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [competition, setCompetition] = useState(null);
  const [bestScores, setBestScores] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPlayers, setExpandedPlayers] = useState({});
  const [showEditRound, setShowEditRound] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);
  const [editRoundDate, setEditRoundDate] = useState('');
  const [editCourseHandicap, setEditCourseHandicap] = useState('');
  const [editScores, setEditScores] = useState([]);
  const [roundsSortOrder, setRoundsSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [socialPostsCount, setSocialPostsCount] = useState(0);
  const [hasViewedSocial, setHasViewedSocial] = useState(false);
  const [course, setCourse] = useState(null);  // Phase B - par-aware data when comp has course_id
  
  // Get initial tab from URL query parameter
  const initialTab = searchParams.get('tab') || 'best';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Helper function to get hole range based on competition format
  const getHoleRange = (holeFormat) => {
    if (holeFormat === "front_9") {
      return { start: 1, end: 9, total: 9 };
    } else if (holeFormat === "back_9") {
      return { start: 10, end: 18, total: 9 };
    } else {
      return { start: 1, end: 18, total: 18 };
    }
  };

  const togglePlayerDetails = (playerId) => {
    setExpandedPlayers(prev => ({
      ...prev,
      [playerId]: !prev[playerId]
    }));
  };

  // Sort rounds based on selected order
  const getSortedRounds = () => {
    const sortedRounds = [...rounds];
    if (roundsSortOrder === 'newest') {
      // Sort by date descending (newest first), then by round_number descending
      sortedRounds.sort((a, b) => {
        const dateCompare = new Date(b.round_date) - new Date(a.round_date);
        if (dateCompare !== 0) return dateCompare;
        return b.round_number - a.round_number;
      });
    } else {
      // Sort by date ascending (oldest first), then by round_number ascending
      sortedRounds.sort((a, b) => {
        const dateCompare = new Date(a.round_date) - new Date(b.round_date);
        if (dateCompare !== 0) return dateCompare;
        return a.round_number - b.round_number;
      });
    }
    return sortedRounds;
  };

  const toggleSortOrder = () => {
    setRoundsSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest');
  };

  useEffect(() => {
    if (user) {
      fetchData();
      fetchUnreadMessages();
      fetchCompetitions();
    }
  }, [user, id]);

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions`, { withCredentials: true });
      setCompetitions(response.data);
    } catch (error) {
      console.error("Failed to load competitions:", error);
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      const [generalResp, compResp] = await Promise.all([
        axios.get(`${API}/messages/unread`, { withCredentials: true }),
        axios.get(`${API}/messages/unread?competition_id=${id}`, { withCredentials: true })
      ]);
      const allUnread = [...generalResp.data, ...compResp.data];
      setUnreadMessages(allUnread);
      if (allUnread.length > 0) {
        setShowMessagePopup(true);
      }
    } catch (error) {
      console.error("Failed to load unread messages:", error);
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/mark-read`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Failed to mark message as read:", error);
    }
  };

  const handleClosePopup = () => {
    setShowMessagePopup(false);
    setUnreadMessages([]);
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message from your view?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/player/messages/${messageId}`, { withCredentials: true });
      toast.success("Message deleted");
      fetchData(); // Refresh messages
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const handleBulkDeleteMessages = async () => {
    if (!window.confirm("Are you sure you want to delete all competition messages?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/player/messages/bulk?competition_id=${id}`, {
        withCredentials: true
      });
      toast.success("All messages deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete messages");
    }
  };

  const handleOpenEditRound = (round) => {
    setSelectedRound(round);
    setEditRoundDate(round.round_date);
    setEditCourseHandicap(round.course_handicap || '');
    setEditScores(round.scores);
    setShowEditRound(true);
  };

  const handleEditScoreChange = (holeIndex, value) => {
    const newScores = [...editScores];
    newScores[holeIndex].strokes = parseInt(value) || 0;
    setEditScores(newScores);
  };

  const handleUpdateRound = async (e) => {
    e.preventDefault();
    
    // Validate handicap
    if (editCourseHandicap === '' || parseInt(editCourseHandicap) < 0 || parseInt(editCourseHandicap) > 54) {
      toast.error("Please enter a valid Course Handicap (0-54)");
      return;
    }
    
    // Validate scores
    const invalidScores = editScores.filter(s => s.strokes <= 0 || s.strokes > 10);
    if (invalidScores.length > 0) {
      toast.error("Please enter valid scores (1-10) for all holes");
      return;
    }
    
    // Feb 2026 UX guard: if the date change would push this round earlier
    // than every OTHER round the player has, it becomes the new baseline
    // (Round 1). Ask for explicit confirmation because most players
    // won't realise the implication — the backend already enforces the
    // all-18-holes rule, but a friendly heads-up prevents unhappy surprises.
    const dateChanged = selectedRound?.round_date !== editRoundDate;
    if (dateChanged && rounds && rounds.length > 1) {
      const otherEarliest = rounds
        .filter((r) => r.id !== selectedRound.id)
        .reduce((min, r) => (min === null || r.round_date < min ? r.round_date : min), null);
      if (otherEarliest && editRoundDate < otherEarliest) {
        const confirmed = window.confirm(
          "You've changed the date of this round to " + editRoundDate +
          ", which is earlier than every other round you've entered.\n\n" +
          "This will make this round the new BASELINE (Round 1) — all your other rounds " +
          "will be re-numbered and your best scores rebuilt from this round forward.\n\n" +
          "Did you mean to do this? Click OK to save the new date, or Cancel to keep the original date."
        );
        if (!confirmed) return;
      }
    }

    try {
      await axios.put(
        `${API}/competitions/${id}/my-rounds/${selectedRound.id}`,
        {
          round_date: editRoundDate,
          course_handicap: parseInt(editCourseHandicap),
          scores: editScores
        },
        { withCredentials: true }
      );
      toast.success("Round updated successfully");
      setShowEditRound(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Update round error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to update round");
    }
  };

  const handleDeleteRound = async (roundId) => {
    if (!window.confirm("Are you sure you want to delete this round? This will recalculate your best scores.")) {
      return;
    }

    try {
      await axios.delete(`${API}/competitions/${id}/my-rounds/${roundId}`, {
        withCredentials: true
      });
      toast.success("Round deleted successfully");
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Delete round error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to delete round");
    }
  };

  const fetchData = async () => {
    try {
      // CRITICAL: we used to fetch the entire `/competitions` joinable list
      // and `.find(c => c.id === id)` from it. That broke once the joinable
      // list started excluding ended comps (May-21 fix) — players couldn't
      // open their own past competitions because the lookup returned
      // `undefined` and the page hung on "loading...". Switch to the new
      // single-comp endpoint which authorises by participation and works
      // for active, upcoming, ended AND archived comps the player took
      // part in.
      const [compResp, bestResp, roundsResp, leaderResp, messagesResp] = await Promise.all([
        axios.get(`${API}/competitions/${id}`, { withCredentials: true }),
        axios.get(`${API}/competitions/${id}/my-best`, { withCredentials: true }),
        axios.get(`${API}/competitions/${id}/my-rounds`, { withCredentials: true }),
        axios.get(`${API}/competitions/${id}/leaderboard`, { withCredentials: true }),
        axios.get(`${API}/competitions/${id}/messages`, { withCredentials: true })
      ]);

      const comp = compResp.data;
      setCompetition(comp);
      setBestScores(bestResp.data);
      setRounds(roundsResp.data);
      setLeaderboard(leaderResp.data);
      setMessages(messagesResp.data);

      // Phase B - if competition is linked to a course, fetch par data
      if (comp?.course_id) {
        try {
          const courseResp = await axios.get(`${API}/courses/${comp.course_id}`, { withCredentials: true });
          setCourse(courseResp.data);
        } catch (e) {
          // Non-fatal — graceful fallback to no par display
          setCourse(null);
        }
      } else {
        setCourse(null);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBestScorePDF = async () => {
    try {
      const response = await axios.get(`${API}/competitions/${id}/my-best-pdf`, {
        responseType: 'blob',
        withCredentials: true
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `My_Best_Score_${competition.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Best Score PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleDownloadLeaderboardPDF = async () => {
    try {
      const response = await axios.get(`${API}/competitions/${id}/leaderboard-pdf`, {
        responseType: 'blob',
        withCredentials: true
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${competition.name}_Leaderboard.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Leaderboard PDF downloaded");
    } catch (error) {
      // Handle blob error response
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          toast.error(errorData.detail || "Failed to download PDF");
        } catch {
          toast.error("Failed to download PDF");
        }
      } else {
        toast.error(error.response?.data?.detail || "Failed to download PDF");
      }
    }
  };

  const handleDownloadRoundsExcel = async () => {
    try {
      const response = await axios.get(`${API}/competitions/${id}/my-rounds-excel`, {
        responseType: 'blob',
        withCredentials: true
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `My_Rounds_${competition.name}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Rounds Excel downloaded");
    } catch (error) {
      toast.error("Failed to download Excel");
    }
  };

  if (!user || loading || !competition) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-ink">Loading...</div>
      </div>
    );
  }

  const holeScoresMap = bestScores?.hole_scores?.reduce((acc, hs) => {
    acc[hs.hole] = hs;
    return acc;
  }, {}) || {};

  return (
    <div className="min-h-screen bg-app">
      {/* Header */}
      <header className="bg-white border-b border-line py-4 px-4 sm:py-6 sm:px-8 shadow-sm">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Logo variant="icon" size={36} />
            <h1 className="text-lg sm:text-xl font-semibold text-ink tracking-tight">Golf Eclectic</h1>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="flex items-center space-x-1 sm:space-x-2 text-sm sm:text-base"
            size="sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 sm:px-8 sm:py-12 max-w-6xl pb-32">
        <div className="mb-6 sm:mb-8">
          <p className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-muted mb-2">{competition.name}</p>
          <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-ink leading-[1.05] mb-2">Your competition</h2>
          <p className="text-base sm:text-lg text-ink-muted">
            {rounds.length} of {competition.total_rounds} rounds played
            {competition.end_date && (
              <>
                <span className="mx-2 text-ink-soft">·</span>
                ends {formatDateUK(competition.end_date)}
              </>
            )}
          </p>
        </div>

        {/* Stat band */}
        {(() => {
          // Match the player's own row in the leaderboard. Backend now returns user_email + user_id
          // explicitly so we always find ourselves once the leaderboard is populated.
          const yourEntry = leaderboard.find(
            (p) => (p.user_email && user?.email && p.user_email === user.email) ||
                   (p.user_id && user?.id && p.user_id === user.id)
          );
          const totalPlayers = leaderboard.length;
          const myRank = yourEntry?.rank || null;
          // If we're on the leaderboard, trust that net. Otherwise compute locally from our
          // gross best + the most recent round's course_handicap (preserves the ÷2 convention).
          const latestRoundHcp = rounds.length > 0
            ? (rounds.slice().sort((a, b) => new Date(b.round_date || b.played_at || 0) - new Date(a.round_date || a.played_at || 0))[0]?.course_handicap ?? 0)
            : 0;
          const grossTotal = bestScores?.total_score ?? null;
          const computedNet = grossTotal != null
            ? Number((grossTotal - (latestRoundHcp / 2)).toFixed(1))
            : null;
          const myNet = yourEntry?.net_score ?? computedNet;
          const ahead = myRank && myRank > 1 ? leaderboard[myRank - 2] : null;
          const strokesFromAhead = ahead && yourEntry ? Math.max(0, yourEntry.net_score - ahead.net_score) : null;
          const bestHole = (bestScores?.hole_scores || []).reduce((acc, h) => (!acc || h.strokes < acc.strokes ? h : acc), null);
          return (
            <div className="grid grid-cols-2 gap-px bg-line border border-line rounded-2xl overflow-hidden mb-8" data-testid="comp-stat-band">
              <div className="bg-surface px-4 py-4 sm:px-5 sm:py-5">
                <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Eclectic Net</div>
                <div className="font-mono tabular-nums text-[34px] sm:text-[38px] font-semibold tracking-tight leading-none mt-2 text-accent" data-testid="stat-eclectic-net">{myNet ?? '—'}</div>
                <div className="text-[12px] mt-2.5 text-ink-muted">{grossTotal ? `Gross ${grossTotal}` : 'Awaiting first round'}</div>
              </div>
              <div className="bg-surface px-4 py-4 sm:px-5 sm:py-5">
                <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Position</div>
                <div className="font-mono tabular-nums text-[34px] sm:text-[38px] font-semibold tracking-tight leading-none mt-2">
                  {myRank ?? '—'}
                  {myRank && totalPlayers > 0 && <span className="text-ink-soft text-[24px] ml-0.5">/{totalPlayers}</span>}
                </div>
                <div className="text-[12px] mt-2.5 text-ink-muted">
                  {strokesFromAhead != null && strokesFromAhead > 0
                    ? `${strokesFromAhead} stroke${strokesFromAhead === 1 ? '' : 's'} from ${myRank - 1 === 1 ? '1st' : `${myRank - 1}th`}`
                    : myRank === 1 ? 'Leading the field' : 'No leaderboard yet'}
                </div>
              </div>
              <div className="bg-surface px-4 py-4 sm:px-5 sm:py-5">
                <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Rounds</div>
                <div className="font-mono tabular-nums text-[34px] sm:text-[38px] font-semibold tracking-tight leading-none mt-2">
                  {rounds.length}
                  <span className="text-ink-soft text-[24px] ml-0.5">/{competition.total_rounds}</span>
                </div>
                <div className="text-[12px] mt-2.5 text-ink-muted">{Math.max(0, competition.total_rounds - rounds.length)} left in this competition</div>
              </div>
              <div className="bg-surface px-4 py-4 sm:px-5 sm:py-5">
                <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-ink-muted">Best hole</div>
                <div className="font-mono tabular-nums text-[34px] sm:text-[38px] font-semibold tracking-tight leading-none mt-2">
                  {bestHole ? bestHole.strokes : '—'}
                  {bestHole && <span className="text-ink-muted text-[20px] ml-1.5 font-medium">on {bestHole.hole}</span>}
                </div>
                <div className="text-[12px] mt-2.5 text-ink-muted">
                  {bestHole?.strokes === 1 ? <span className="text-warm font-semibold">Hole-in-one · {formatDateUK(bestHole.round_date)}</span>
                    : bestHole?.strokes === 2 && competition?.recognize_score_of_2 ? <span className="text-warm font-semibold">Excellent 2 · {formatDateUK(bestHole.round_date)}</span>
                    : bestHole ? formatDateUK(bestHole.round_date) : 'No rounds yet'}
                </div>
              </div>
            </div>
          );
        })()}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-2 sm:grid-cols-5 w-full max-w-4xl gap-2 h-auto bg-transparent p-0 sm:p-1 sm:bg-muted">
            <TabsTrigger value="best" className="w-full py-3 text-sm sm:text-base font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Eclectic Score
            </TabsTrigger>
            <TabsTrigger value="rounds" className="w-full py-3 text-sm sm:text-base font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
              All Rounds
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="w-full py-3 text-sm sm:text-base font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Leaderboard
            </TabsTrigger>
            <TabsTrigger 
              value="social" 
              className="w-full py-3 text-sm sm:text-base font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm"
              onClick={() => setHasViewedSocial(true)}
            >
              <Users className="w-4 h-4 mr-1 sm:mr-2" />
              Social
              {!hasViewedSocial && socialPostsCount > 0 && (
                <span className="ml-2 bg-ink text-white text-xs rounded-full px-2 py-0.5">
                  {socialPostsCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="messages" className="w-full py-3 text-sm sm:text-base font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Messages
              {unreadMessages.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                  {unreadMessages.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Best Scores Tab */}
          <TabsContent value="best">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-2xl">Best Eclectic Score</CardTitle>
                  <div className="flex items-center gap-3">
                    {bestScores && bestScores.total_score > 0 && (
                      <>
                        <Button
                          onClick={handleDownloadBestScorePDF}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                          data-testid="download-best-pdf-btn"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download PDF</span>
                        </Button>
                        <div className="text-right">
                          <p className="ge-stat-label">Total Score</p>
                          <p className="text-4xl font-semibold tracking-tight text-ink tabular-nums leading-none mt-1">{bestScores.total_score}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {bestScores && bestScores.hole_scores && bestScores.hole_scores.length > 0 ? (
                  <>
                    {/* Phase B - Par-aware summary strip when course is linked */}
                    {course && (() => {
                      const holeFormat = competition?.hole_format || "18";
                      const { start, end } = getHoleRange(holeFormat);
                      // Pick which tee to use: explicit comp.tee_id, else first tee
                      const teeList = course.tees || [];
                      const tee = (competition?.tee_id && teeList.find(t => t.id === competition.tee_id)) || teeList[0];
                      if (!tee) return null;
                      const totalPar = (tee.holes || [])
                        .filter(h => h.hole >= start && h.hole <= end)
                        .reduce((s, h) => s + (parseInt(h.par) || 0), 0);
                      const playedTotal = bestScores.total_score || 0;
                      const diff = playedTotal - totalPar;
                      const diffStr = diff === 0 ? "Even" : (diff > 0 ? `+${diff}` : `${diff}`);
                      return (
                        <div className="mb-5 flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-app border border-line text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <Trophy className="w-4 h-4 text-accent flex-shrink-0" />
                            <span className="truncate text-ink-muted">
                              {course.club_name && course.club_name !== course.name && (
                                <><span className="text-ink font-medium">{course.club_name}</span><span className="text-ink-soft"> · </span></>
                              )}
                              <span className="text-ink font-medium">{course.name}</span>
                              {tee.name ? <span className="text-ink-soft"> · {tee.name}</span> : null}
                              {course.location ? <span className="text-ink-soft hidden sm:inline"> · {course.location}</span> : null}
                            </span>
                          </div>
                          <div className="font-mono tabular-nums text-[13px] text-ink-muted whitespace-nowrap">
                            par <span className="text-ink font-semibold">{totalPar}</span>
                            <span className="mx-2 text-ink-soft">·</span>
                            <span className={diff < 0 ? "text-accent font-semibold" : "text-ink"}>{diffStr} to par</span>
                          </div>
                        </div>
                      );
                    })()}
                    {/* Inline legend so colours are obvious */}
                    {course && (
                      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-ink-muted">
                        <span className="font-semibold tracking-[0.06em] uppercase text-ink-soft">Score key</span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-accent inline-block"></span>
                          <span>Under par</span>
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-ink inline-block"></span>
                          <span>Par</span>
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-ink-soft inline-block"></span>
                          <span>Over par</span>
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full ring-2 ring-warm bg-warm-tint inline-block"></span>
                          <span>Hole-in-one / Excellent 2</span>
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                      {(() => {
                        const holeFormat = competition?.hole_format || "18";
                        const { start, end } = getHoleRange(holeFormat);
                        const teeList = course?.tees || [];
                        const tee = (competition?.tee_id && teeList.find(t => t.id === competition.tee_id)) || teeList[0];
                        return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(hole => {
                          const holeData = holeScoresMap[hole];
                          const isHoleInOne = holeData && holeData.strokes === 1;
                          const isScoreOfTwo = holeData && holeData.strokes === 2 && competition?.recognize_score_of_2;
                          const par = tee?.holes?.find(h => h.hole === hole)?.par;
                          const isSpecial = isHoleInOne || isScoreOfTwo;
                          const isUnderPar = !isSpecial && par && holeData && holeData.strokes < par;
                          const isOverPar = !isSpecial && par && holeData && holeData.strokes > par;
                          const scoreColor = isSpecial
                            ? 'text-warm'
                            : isUnderPar
                              ? 'text-accent'
                              : isOverPar
                                ? 'text-ink-muted'
                                : 'text-ink';
                          return (
                            <div
                              key={hole}
                              data-testid={`best-hole-${hole}`}
                              className={`hole-card rounded-xl p-3 transition-colors ${
                                isSpecial
                                  ? 'ring-2 ring-warm bg-warm-tint/40 border border-warm'
                                  : 'border border-line'
                              }`}
                            >
                              {/* Header: hole label + clearly-labelled par chip */}
                              <div className="flex items-center justify-between mb-2.5">
                                <span className="text-[12px] font-semibold tracking-[0.06em] uppercase text-ink">
                                  Hole {hole}
                                </span>
                                {par ? (
                                  <span className="font-mono tabular-nums text-[10.5px] font-semibold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-md bg-app text-ink-muted border border-line">
                                    Par {par}
                                  </span>
                                ) : null}
                              </div>
                              {/* Score */}
                              {holeData ? (
                                <>
                                  <div className="flex items-baseline gap-1.5">
                                    <span className={`font-mono tabular-nums text-[34px] font-semibold tracking-tight leading-none ${scoreColor}`}>
                                      {holeData.strokes}
                                    </span>
                                    {par && !isSpecial && (
                                      <span className={`text-[12px] font-mono font-semibold ${
                                        isUnderPar ? 'text-accent' : isOverPar ? 'text-ink-muted' : 'text-ink-muted'
                                      }`}>
                                        {holeData.strokes - par === 0 ? 'E' : (holeData.strokes - par > 0 ? `+${holeData.strokes - par}` : `${holeData.strokes - par}`)}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[12px] text-ink-muted mt-2 font-medium">{formatDateUK(holeData.round_date)}</div>
                                  {isHoleInOne && (
                                    <div className="text-[10.5px] text-warm font-semibold mt-1 tracking-wide uppercase">
                                      Hole-in-one
                                    </div>
                                  )}
                                  {isScoreOfTwo && (
                                    <div className="text-[10.5px] text-warm font-semibold mt-1 tracking-wide uppercase">
                                      Excellent 2
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="text-2xl font-semibold tabular-nums text-ink-soft">—</div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                    {rounds.length < competition.total_rounds && (
                      <div className="flex justify-center mt-6">
                        <Button
                          onClick={() => navigate(`/player/competitions/${id}/enter-score`)}
                          className="bg-ink hover:bg-ink-hover"
                        >
                          ➕ Enter Another Round
                        </Button>
                      </div>
                    )}
                    {rounds.length >= competition.total_rounds && (
                      <div className="flex justify-center mt-6">
                        <p className="text-sm text-ink-soft">
                          You have completed all {competition.total_rounds} rounds for this competition.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-ink-muted">
                    <TrendingUp className="w-12 h-12 text-ink-soft mx-auto mb-3" />
                    <p>No rounds submitted yet</p>
                    <Button
                      onClick={() => navigate(`/player/competitions/${id}/enter-score`)}
                      className="mt-4 bg-ink hover:bg-ink-hover"
                    >
                      Enter Your First Round
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Rounds Tab */}
          <TabsContent value="rounds">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                    <CardTitle className="text-xl sm:text-2xl">All Rounds</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={toggleSortOrder}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 text-xs sm:text-sm"
                      >
                        <ArrowUpDown className="w-4 h-4" />
                        <span className="hidden sm:inline">{roundsSortOrder === 'newest' ? 'Newest First' : 'Oldest First'}</span>
                        <span className="sm:hidden">{roundsSortOrder === 'newest' ? 'Newest' : 'Oldest'}</span>
                      </Button>
                      {rounds.length > 0 && (
                        <Button
                          onClick={handleDownloadRoundsExcel}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2 text-xs sm:text-sm"
                        >
                          <Download className="w-4 h-4" />
                          <span className="hidden sm:inline">Download Excel</span>
                          <span className="sm:hidden">Excel</span>
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {rounds.length < competition.total_rounds && (
                      <Button
                        onClick={() => navigate(`/player/competitions/${id}/enter-score`)}
                        className="bg-ink hover:bg-ink-hover w-full sm:w-auto"
                        size="sm"
                      >
                        ➕ Enter Another Round
                      </Button>
                    )}
                    {rounds.length >= competition.total_rounds && (
                      <p className="text-xs sm:text-sm text-ink-soft text-center sm:text-right">
                        All {competition.total_rounds} rounds completed
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Admin-verified final handicap notice — explains why round HCPs may differ from leaderboard */}
                {(() => {
                  const yourLb = leaderboard.find(
                    (p) => (p.user_email && user?.email && p.user_email === user.email) ||
                           (p.user_id && user?.id && p.user_id === user.id)
                  );
                  const verifiedHcp = yourLb?.course_handicap;
                  if (verifiedHcp == null || rounds.length === 0) return null;
                  const anyDiffer = rounds.some(
                    (r) => r.course_handicap != null && r.course_handicap !== verifiedHcp
                  );
                  if (!anyDiffer) return null;
                  return (
                    <div
                      data-testid="verified-hcp-notice"
                      className="mb-4 rounded-lg border border-accent/30 bg-accent-tint p-3 text-[12.5px] text-accent-deep flex items-start gap-2"
                    >
                      <span className="font-semibold tabular-nums">i</span>
                      <p>
                        Your <strong>verified final handicap is {verifiedHcp}</strong> — this is what the leaderboard, PDFs and certificates use.
                        Each round below shows the handicap you played with on the day, which is normal and not an error.
                      </p>
                    </div>
                  );
                })()}
                {rounds.length > 0 ? (
                  <div className="space-y-4">
                    {getSortedRounds().map((round, index) => {
                      const teeListR = course?.tees || [];
                      const teeR = (competition?.tee_id && teeListR.find(t => t.id === competition.tee_id)) || teeListR[0];
                      const recogTwo = competition?.recognize_score_of_2;
                      let underN = 0, parN = 0, overN = 0, specialN = 0;
                      round.scores.forEach(s => {
                        const isHIO = s.strokes === 1;
                        const isEx2 = s.strokes === 2 && recogTwo;
                        if (isHIO || isEx2) { specialN++; return; }
                        const p = teeR?.holes?.find(h => h.hole === s.hole)?.par;
                        if (!p) return;
                        if (s.strokes < p) underN++;
                        else if (s.strokes > p) overN++;
                        else parN++;
                      });
                      return (
                      <div
                        key={round.id}
                        data-testid={`round-${round.round_number}`}
                        className="bg-app rounded-lg p-4 border border-line"
                      >
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-ink">Round {round.round_number}</h4>
                            <p className="text-sm text-ink-muted">{formatDateUK(round.round_date)}</p>
                            <p className="text-sm font-semibold text-accent-deep mt-1">
                              Course Handicap: {round.course_handicap ?? 'Not recorded'}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-sm text-ink-muted">Total</p>
                              <p className="text-xl font-semibold text-ink tracking-tight">
                                {round.scores.reduce((sum, s) => sum + s.strokes, 0)}
                              </p>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenEditRound(round);
                                }}
                                className="flex items-center gap-1"
                              >
                                <Edit className="w-4 h-4" />
                                <span>Edit</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRound(round.id);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-1"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Delete</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div
                          className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-ink-muted"
                          data-testid={`round-${round.round_number}-summary`}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-accent inline-block" />
                            <b className="font-mono tabular-nums text-ink">{underN}</b> under
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-ink inline-block" />
                            <b className="font-mono tabular-nums text-ink">{parN}</b> at par
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-ink-soft inline-block" />
                            <b className="font-mono tabular-nums text-ink">{overN}</b> over
                          </span>
                          {specialN > 0 && (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full ring-2 ring-warm bg-warm-tint inline-block" />
                              <b className="font-mono tabular-nums text-warm">{specialN}</b> {recogTwo ? 'HIO/Ex2' : 'HIO'}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-6 md:grid-cols-9 gap-2">
                          {round.scores.map(score => {
                            const isHoleInOne = score.strokes === 1;
                            const isScoreOfTwo = score.strokes === 2 && competition?.recognize_score_of_2;
                            const isSpecial = isHoleInOne || isScoreOfTwo;
                            // Pull the per-hole par from the same tee used everywhere else.
                            const teeList = course?.tees || [];
                            const tee = (competition?.tee_id && teeList.find(t => t.id === competition.tee_id)) || teeList[0];
                            const par = tee?.holes?.find(h => h.hole === score.hole)?.par;
                            const isUnderPar = !isSpecial && par && score.strokes < par;
                            const isOverPar  = !isSpecial && par && score.strokes > par;
                            const tone = isSpecial ? 'text-warm'
                              : isUnderPar ? 'text-accent'
                              : isOverPar ? 'text-ink-soft'
                              : 'text-ink';
                            return (
                              <div
                                key={score.hole}
                                data-testid={`round-${round.id || round.round_number}-hole-${score.hole}`}
                                className={`text-center rounded p-1 ${
                                  isSpecial ? 'ring-2 ring-warm bg-warm-tint/40' : ''
                                }`}
                              >
                                <div className="text-xs text-ink-soft">H{score.hole}</div>
                                <div className={`font-semibold ${tone}`}>{score.strokes}</div>
                                {par && !isSpecial && (
                                  <div className="text-[9.5px] text-ink-soft font-mono tabular-nums">
                                    {score.strokes - par === 0 ? 'E' : (score.strokes - par > 0 ? `+${score.strokes - par}` : `${score.strokes - par}`)}
                                  </div>
                                )}
                                {isHoleInOne && <div className="text-[9.5px] text-warm font-semibold uppercase tracking-wide">HIO</div>}
                                {isScoreOfTwo && <div className="text-[9.5px] text-warm font-semibold uppercase tracking-wide">EX 2</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-ink-muted">
                    No rounds recorded yet
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-2xl flex items-center space-x-2">
                    <Award className="w-6 h-6 text-accent" />
                    <span>Full Leaderboard</span>
                  </CardTitle>
                  {leaderboard.length > 0 && (
                    <Button
                      onClick={handleDownloadLeaderboardPDF}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download PDF</span>
                    </Button>
                  )}
                </div>
                <p className="text-sm text-ink-muted mt-2">
                  Rankings based on Net Scores (Gross - Handicap ÷ 2)
                </p>
              </CardHeader>
              <CardContent>
                {leaderboard.length > 0 ? (
                  <div className="border border-line rounded-xl overflow-hidden">
                    {leaderboard.map((player, index) => {
                      const playerId = player.player_id || index;
                      const showDetails = expandedPlayers[playerId] || false;
                      const isYou = user && (player.user_email === user.email || player.player_id === user.id);
                      const initials = (player.player_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
                      
                      return (
                        <div
                          key={playerId}
                          data-testid={`leaderboard-rank-${player.rank}`}
                          className={`border-b border-line last:border-b-0 ${
                            isYou ? "bg-accent-tint-2 relative" : "bg-surface"
                          }`}
                        >
                          {isYou && <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent" />}
                          {/* Main row */}
                          <div 
                            className="px-4 py-3.5 cursor-pointer hover:bg-app transition-colors"
                            onClick={() => togglePlayerDetails(playerId)}
                          >
                            <div className="flex justify-between items-center gap-3">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className={`tabular-nums text-sm font-semibold w-7 text-center ${
                                  index === 0 ? "text-warm" : index < 3 ? "text-ink" : "text-ink-muted"
                                }`}>{player.rank}</div>
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                                  index === 0 ? "bg-warm-tint text-warm" :
                                  isYou ? "bg-accent text-white" :
                                  "bg-accent-tint text-accent-deep"
                                }`}>
                                  {initials}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-ink text-[14.5px] tracking-tight truncate">
                                    {player.player_name}
                                    {isYou && <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-accent text-white text-[10px] font-semibold tracking-wider align-middle">YOU</span>}
                                  </p>
                                  <p className="text-[12px] text-ink-muted">
                                    {showDetails ? "Tap to hide" : "Tap for hole-by-hole"}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-5 text-right flex-shrink-0">
                                <div className="hidden sm:block">
                                  <p className="ge-stat-label">Gross</p>
                                  <p className="text-[15px] tabular-nums font-medium text-ink-muted leading-tight">{player.gross_score}</p>
                                </div>
                                <div className="hidden sm:block">
                                  <p className="ge-stat-label">H/cap</p>
                                  <p className="text-[15px] tabular-nums font-medium text-ink-muted leading-tight">{player.course_handicap}</p>
                                </div>
                                <div>
                                  <p className="ge-stat-label">Net</p>
                                  <p className="text-[20px] tabular-nums font-semibold text-ink leading-tight">{player.net_score}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Expandable details */}
                          {showDetails && (
                            <div className="px-4 pb-4 border-t border-line pt-4 bg-app">
                              <p className="ge-stat-label mb-3">Best Scores by Hole</p>
                              <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-9 gap-1.5">
                                {(() => {
                                  const holeFormat = competition?.hole_format || "18";
                                  const { start, end } = getHoleRange(holeFormat);
                                  return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(hole => {
                                    const holeScore = player.hole_scores?.find(hs => hs.hole === hole);
                                    const isHoleInOne = holeScore && holeScore.strokes === 1;
                                    const isScoreOfTwo = holeScore && holeScore.strokes === 2 && competition?.recognize_score_of_2;
                                    return (
                                      <div key={hole} className={`text-center rounded-md px-1 py-1.5 border ${
                                        isHoleInOne ? 'bg-warm-tint/40 border-warm/40' :
                                        isScoreOfTwo ? 'bg-accent-tint-2 border-accent' :
                                        'bg-surface border-line'
                                      }`}>
                                        <div className="text-[10px] text-ink-muted font-semibold">H{hole}</div>
                                        <div className={`text-[15px] font-semibold tabular-nums ${
                                          isHoleInOne ? 'text-warm' :
                                          isScoreOfTwo ? 'text-accent-deep' :
                                          'text-ink'
                                        }`}>
                                          {holeScore ? holeScore.strokes : "—"}
                                        </div>
                                        {holeScore && (
                                          <div className="text-[10px] text-ink-soft mt-0.5">
                                            {formatDateUK(holeScore.round_date)}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  });
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-ink-muted">
                    <Award className="w-10 h-10 text-ink-soft mx-auto mb-3" />
                    <p>No scores recorded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Social Tab */}
          <TabsContent value="social">
            <SocialFeed 
              competitionId={id} 
              onPostsCountChange={setSocialPostsCount}
            />
          </TabsContent>
          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-2xl flex items-center space-x-2">
                    <MessageSquare className="w-6 h-6 text-accent-deep" />
                    <span>Competition Messages</span>
                  </CardTitle>
                  <Button
                    onClick={handleBulkDeleteMessages}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    disabled={messages.length === 0}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {messages.length > 0 ? (
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`border-l-4 rounded-lg p-4 ${
                          message.is_read 
                            ? 'bg-app border-line-strong' 
                            : 'bg-app border-ink'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-lg text-ink">{message.title}</h4>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              message.priority === 'high' 
                                ? 'bg-red-100 text-red-800'
                                : message.priority === 'medium'
                                ? 'bg-warm-tint/60 text-warm'
                                : 'bg-accent-tint text-accent-deep'
                            }`}>
                              {message.priority?.toUpperCase() || 'MEDIUM'}
                            </span>
                            {!message.is_read && (
                              <span className="text-xs bg-ink text-white px-2 py-1 rounded">
                                NEW
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-ink-soft">
                              {formatDateUK(new Date(message.created_at).toISOString().split('T')[0])}
                            </span>
                            <Button
                              onClick={() => handleDeleteMessage(message.id)}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-ink-muted whitespace-pre-wrap">{message.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-ink-muted">
                    <MessageSquare className="w-12 h-12 text-ink-soft mx-auto mb-3" />
                    <p>No messages posted yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Sticky Enter Score CTA */}
      {!competition?.handicaps_locked && rounds.length < competition?.total_rounds && (
        <div className="fixed inset-x-0 bottom-0 z-30 pointer-events-none">
          <div className="container mx-auto max-w-6xl px-4 sm:px-8 pb-5 flex justify-end">
            <button
              type="button"
              data-testid="sticky-enter-score-btn"
              onClick={() => navigate(`/player/competitions/${id}/enter-score`)}
              className="pointer-events-auto inline-flex items-center gap-2 bg-ink text-white font-semibold pl-4 pr-5 py-3.5 rounded-full shadow-lg shadow-ink/15 hover:bg-ink-hover transition-all"
            >
              <Plus className="w-5 h-5" />
              <span>Enter score</span>
            </button>
          </div>
        </div>
      )}

      {/* Message Popup */}
      {showMessagePopup && unreadMessages.length > 0 && (
        <MessagePopup
          messages={unreadMessages}
          onClose={handleClosePopup}
          onMarkRead={handleMarkRead}
        />
      )}

      {/* Edit Round Dialog */}
      <Dialog open={showEditRound} onOpenChange={setShowEditRound}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit Round {selectedRound?.round_number}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRound} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-round-date">Round Date</Label>
                <Input
                  id="edit-round-date"
                  type="date"
                  value={editRoundDate}
                  onChange={(e) => setEditRoundDate(e.target.value)}
                  required
                  min={competition?.start_date}
                  max={competition?.end_date && new Date(competition.end_date) < new Date() 
                    ? competition.end_date 
                    : getLocalTodayStr()}
                  className="max-w-xs"
                />
              </div>
              <div>
                <Label htmlFor="edit-course-handicap">Course Handicap</Label>
                <Input
                  id="edit-course-handicap"
                  type="number"
                  min="0"
                  max="54"
                  value={editCourseHandicap}
                  onChange={(e) => setEditCourseHandicap(e.target.value)}
                  required
                  placeholder="e.g., 12"
                  className="max-w-xs"
                />
              </div>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-3 block">
                Hole Scores (1-10 strokes per hole)
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {editScores.map((score, index) => {
                  // Find best score for this hole
                  const bestScore = bestScores?.hole_scores?.find(h => h.hole === score.hole);
                  const currentBest = bestScore?.strokes || 0;
                  
                  return (
                    <div key={score.hole} className="hole-card p-3 rounded-lg border border-line bg-white">
                      <Label className="text-sm font-semibold text-ink-muted mb-2 block text-center">
                        Hole {score.hole}
                      </Label>
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          type="button"
                          onClick={() => handleEditScoreChange(index, Math.max(0, score.strokes - 1))}
                          disabled={score.strokes === 0}
                          className="h-10 w-10 p-0 bg-surface text-ink border border-line hover:bg-app hover:border-line-strong disabled:opacity-30"
                          size="sm"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <div className={`flex items-center justify-center w-12 h-10 text-lg font-bold rounded border-2 ${
                          score.strokes === 0 ? 'bg-surface-2 text-ink-soft border-line-strong' : 
                          score.strokes === 1 ? 'bg-warm-tint/60 text-warm border-warm/40' : 
                          score.strokes === 2 && competition?.recognize_score_of_2 ? 'bg-accent-tint text-accent-deep border-accent/30' : 
                          'bg-white text-ink border-line-strong'
                        }`}>
                          {score.strokes}
                        </div>
                        <Button
                          type="button"
                          onClick={() => handleEditScoreChange(index, Math.min(10, score.strokes + 1))}
                          disabled={score.strokes === 10}
                          className="h-10 w-10 p-0 bg-ink hover:bg-ink-hover disabled:opacity-30"
                          size="sm"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {score.strokes === 1 && (
                        <div className="text-xs text-warm mt-1 text-center font-medium">
                          🏆 Hole in One!
                        </div>
                      )}
                      {score.strokes === 2 && competition?.recognize_score_of_2 && (
                        <div className="text-xs text-accent-deep mt-1 text-center font-medium">
                          💎 Excellent 2!
                        </div>
                      )}
                      {currentBest > 0 && (
                        <div className="text-xs text-ink-soft mt-1 text-center">
                          Best: {currentBest}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-app p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-ink-muted">Round Total</span>
                <span className="text-xl font-semibold text-ink tracking-tight">
                  {editScores.reduce((sum, s) => sum + s.strokes, 0)}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                className="flex-1 bg-ink hover:bg-ink-hover py-3"
              >
                Save Changes
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditRound(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
